import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { EventTopic, SseEvent } from '@warehouse/shared';
import { useEffect, useRef, useState } from 'react';

import { eventStream, type ConnectionState } from './event-stream';

/**
 * The real-time primitive (ARCHITECTURE §9): pair a TanStack Query key with the
 * SSE topics that can invalidate it, and every list in the building updates
 * itself.
 *
 *     const { data } = useQuery({ queryKey: ['stock', listKey], queryFn: … });
 *     useLiveQuery(['stock', 'pallets'], ['stock']);
 *
 * Invalidation (not patching) is deliberate: the event payload is a summary,
 * while the list row is a joined projection. Re-asking the server is one cheap
 * request and cannot drift from what the database actually holds — silently
 * patching a row from a partial payload is how stock screens start lying.
 *
 * Bursts are coalesced: posting a 200-line pick round emits 200 events, and one
 * refetch afterwards is the correct response to all of them.
 */

export interface UseLiveQueryOptions {
  /** Ignore events that don't concern this view (e.g. another restaurant). */
  filter?: (event: SseEvent) => boolean;
  /** Extra work per matching event (toast, badge counter). */
  onEvent?: (event: SseEvent) => void;
  /** Coalesce window in ms. Default 250. */
  debounceMs?: number;
  enabled?: boolean;
  /** `true` (default) also invalidates keys that merely start with `queryKey`. */
  exact?: boolean;
}

export function useLiveQuery(
  topics: readonly EventTopic[],
  queryKey: QueryKey,
  options: UseLiveQueryOptions = {},
): void {
  const { filter, onEvent, debounceMs = 250, enabled = true, exact = false } = options;
  const queryClient = useQueryClient();

  // Keep the latest callbacks/keys in refs so a caller passing inline lambdas
  // (the normal case) does not tear the subscription down on every render.
  const filterRef = useRef(filter);
  const onEventRef = useRef(onEvent);
  const keyRef = useRef(queryKey);
  filterRef.current = filter;
  onEventRef.current = onEvent;
  keyRef.current = queryKey;

  const topicsKey = [...topics].sort().join(',');

  useEffect(() => {
    if (!enabled || topicsKey.length === 0) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = (): void => {
      timer = null;
      void queryClient.invalidateQueries({ queryKey: keyRef.current, exact });
    };

    const unsubscribe = eventStream.subscribe(topicsKey.split(',') as EventTopic[], (event) => {
      if (filterRef.current && !filterRef.current(event)) return;
      onEventRef.current?.(event);
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, debounceMs);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [topicsKey, enabled, debounceMs, exact, queryClient]);
}

/**
 * Raw event subscription for views that are not a query — the notification
 * bell, the exceptions badge, a live scan counter.
 */
export function useEventStream(
  topics: readonly EventTopic[],
  listener: (event: SseEvent) => void,
  enabled = true,
): void {
  const listenerRef = useRef(listener);
  listenerRef.current = listener;
  const topicsKey = [...topics].sort().join(',');

  useEffect(() => {
    if (!enabled || topicsKey.length === 0) return;
    return eventStream.subscribe(topicsKey.split(',') as EventTopic[], (event) => {
      listenerRef.current(event);
    });
  }, [topicsKey, enabled]);
}

/** Connection state for the header indicator. */
export function useEventStreamState(): ConnectionState {
  const [state, setState] = useState<ConnectionState>(() => eventStream.getState());
  useEffect(() => eventStream.onStateChange(setState), []);
  return state;
}
